import React from "react";
import {Badge} from "../../components/core/Badge.jsx";
import {Icon} from "../../components/core/Icon.jsx";
import {Button} from "../../components/core/Button.jsx";
import {H2,Section,Eyebrow,Glitch,Reveal,Photo,Placeholder,withVersion} from "./Shell.jsx";

/* Cargado bajo demanda: model-viewer pesa ~1 MB, así que solo entra en el
   navegador cuando la ficha tiene un modelo 3D que mostrar (no en el bundle
   principal, que carga en todas las páginas). Autoalojado, sin CDN, igual
   que React/iconos/fuentes (ver build-src/nginx/dp-security-headers.conf). */
const MODEL_VIEWER_SRC="/js/model-viewer.min.js";

function ArViewer({model,poster,alt}){
  React.useEffect(()=>{
    if(typeof window==="undefined"||typeof customElements==="undefined")return;
    if(!customElements.get("model-viewer")&&!document.querySelector(`script[src="${MODEL_VIEWER_SRC}"]`)){
      const s=document.createElement("script");
      s.type="module";
      s.src=MODEL_VIEWER_SRC;
      document.head.appendChild(s);
    }
    // Modelos .glb comprimidos con Draco (ver build-src/build.js) necesitan este
    // decoder; autoalojado en /js/draco/ para no pedirlo a gstatic.com (la CSP
    // del sitio bloquea connect-src fuera de 'self').
    customElements.whenDefined("model-viewer").then(()=>{
      customElements.get("model-viewer").dracoDecoderLocation="/js/draco/";
    });
  },[]);
  return React.createElement("model-viewer",{
    src:withVersion(model.glb),
    "ios-src":model.usdz?withVersion(model.usdz):undefined,
    poster,
    alt,
    ar:"",
    "ar-modes":"webxr scene-viewer quick-look",
    "camera-controls":"",
    "shadow-intensity":"1",
    "auto-rotate":"",
    style:{width:"100%",aspectRatio:"4 / 3",borderRadius:"var(--radius-photo)",background:"var(--neutral-100)",display:"block"}
  });
}

function Gallery({photos}){
  const [open,setOpen]=React.useState(null);
  return <>
    <div className="dp-g3" style={{gap:16}}>
      {photos.map((p,i)=>(
        <button key={i} onClick={()=>setOpen(i)} aria-label={`Ampliar foto: ${p.alt}`}
          style={{border:"none",background:"none",padding:0,cursor:"zoom-in"}}>
          <Photo src={p.src} alt={p.alt} ratio="4 / 3"/>
        </button>
      ))}
    </div>
    {open!==null&&(
      <div role="dialog" aria-modal="true" onClick={()=>setOpen(null)}
        style={{position:"fixed",inset:0,zIndex:90,background:"rgba(4,12,12,.92)",display:"flex",alignItems:"center",justifyContent:"center",padding:24,cursor:"zoom-out"}}>
        <img src={withVersion(photos[open].src)} alt={photos[open].alt} style={{maxWidth:"100%",maxHeight:"100%",borderRadius:"var(--radius-photo)"}}/>
        <button aria-label="Cerrar" onClick={()=>setOpen(null)}
          style={{position:"absolute",top:20,right:20,width:40,height:40,borderRadius:"50%",border:"1px solid rgba(255,255,255,.4)",background:"rgba(255,255,255,.08)",color:"#fff",cursor:"pointer"}}>
          <Icon name="x" size={18} strokeAlign="middle"/>
        </button>
      </div>
    )}
  </>;
}

export function CaseScreen({go,item}){
  const [copied,setCopied]=React.useState(false);
  if(!item) return <Section><p>Caso no encontrado.</p></Section>;

  const cover=item.photos&&item.photos[0];
  const rest=(item.photos||[]).slice(1);

  async function copyLink(){
    try{
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(()=>setCopied(false),2000);
    }catch{/* portapapeles no disponible: el botón simplemente no confirma */}
  }

  return <>
    <Section tone="card" style={{paddingTop:64}}>
      <div className="dp-split" style={{gap:"clamp(32px,5vw,64px)",alignItems:"center"}}>
        <div>
          <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <Eyebrow>Caso</Eyebrow>
            {item.demo&&<Badge tone="warning">Ejemplo</Badge>}
          </div>
          <h1 style={{fontSize:"clamp(30px,4.6vw,42px)",marginTop:16,maxWidth:"20ch"}}><Glitch>{item.title}</Glitch></h1>
          {item.summary&&<p style={{fontSize:17,lineHeight:1.6,color:"var(--fg-2)",marginTop:16,maxWidth:"48ch"}}>{item.summary}</p>}
          {item.tags&&item.tags.length>0&&(
            <div style={{display:"flex",flexWrap:"wrap",gap:8,marginTop:20}}>
              {item.tags.map(t=><Badge key={t}>{t}</Badge>)}
            </div>
          )}
          <div style={{display:"flex",gap:12,marginTop:28,flexWrap:"wrap"}}>
            <Button variant="outline" onClick={copyLink} iconLeft={<Icon name="share-2" size={15} strokeAlign="middle"/>}>
              {copied?"¡Enlace copiado!":"Copiar enlace"}
            </Button>
            <Button onClick={()=>go("contacto")}>Enviar un caso así</Button>
          </div>
        </div>
        {cover&&<Photo src={cover.src} alt={cover.alt} ratio="4 / 3"/>}
      </div>
    </Section>

    {rest.length>0&&(
      <Section>
        <Reveal>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={{color:"var(--cyan-700)"}}><Icon name="images" size={20} strokeAlign="middle"/></span>
            <Eyebrow>Fotos</Eyebrow>
          </div>
          <h2 style={{fontSize:H2,marginTop:14}}>Cómo quedó</h2>
        </Reveal>
        <div style={{marginTop:32}}><Gallery photos={rest}/></div>
      </Section>
    )}

    <Section tone="card">
      <Reveal>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{color:"var(--cyan-700)"}}><Icon name="video" size={20} strokeAlign="middle"/></span>
          <Eyebrow>Vídeo</Eyebrow>
        </div>
        <h2 style={{fontSize:H2,marginTop:14}}>El trabajo en movimiento</h2>
      </Reveal>
      <div style={{marginTop:28}}>
        {item.video?(
          <video controls poster={withVersion(item.video.poster)} style={{width:"100%",borderRadius:"var(--radius-photo)",background:"#000"}}>
            <source src={withVersion(item.video.src)} type="video/mp4"/>
          </video>
        ):(
          <Placeholder label="Sin vídeo todavía · aparecerá aquí en cuanto lo suba" ratio="16 / 9"/>
        )}
      </div>
    </Section>

    <Section>
      <Reveal>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{color:"var(--cyan-700)"}}><Icon name="box" size={20} strokeAlign="middle"/></span>
          <Eyebrow>Realidad aumentada</Eyebrow>
        </div>
        <h2 style={{fontSize:H2,marginTop:14}}>Míralo en tu espacio</h2>
        <p style={{fontSize:15,lineHeight:1.6,color:"var(--fg-2)",marginTop:10,maxWidth:"46ch"}}>Desde el móvil, toca el icono de AR del visor y coloca el modelo 3D sobre la mesa, a tamaño real.</p>
      </Reveal>
      <div style={{marginTop:28}}>
        {item.arModel?(<>
          <ArViewer model={item.arModel} poster={withVersion(cover&&cover.src)} alt={item.title}/>
          <div style={{display:"flex",gap:20,marginTop:14,color:"var(--fg-3)",fontSize:12,fontFamily:"var(--font-mono)",flexWrap:"wrap"}}>
            <span style={{display:"flex",alignItems:"center",gap:6}}><Icon name="rotate-3d" size={14} strokeAlign="middle"/>arrastra para girar</span>
            <span style={{display:"flex",alignItems:"center",gap:6}}><Icon name="maximize" size={14} strokeAlign="middle"/>pantalla completa</span>
          </div>
        </>):(
          <Placeholder label="Sin modelo 3D todavía · aparecerá aquí en cuanto lo suba" ratio="4 / 3"/>
        )}
      </div>
    </Section>

    <Section tone="brand">
      <div style={{textAlign:"center",maxWidth:560,margin:"0 auto"}}>
        <h2 style={{fontSize:H2,color:"#fff"}}>¿Tienes un caso <Glitch>parecido</Glitch>?</h2>
        <p style={{fontSize:16,color:"var(--neutral-200)",marginTop:12}}>Mándamelo y te contesto hoy mismo.</p>
        <div style={{marginTop:24}}>
          <Button size="lg" variant="accent" onClick={()=>go("contacto")} iconRight={<Icon name="arrow-right" size={16}/>}>Enviar un caso</Button>
        </div>
      </div>
    </Section>
  </>;
}
